import type {
  FileItem,
  LeafletTabData
} from '@/views/dashboard/components/AddEditLeafletDialog/domain/leaflet-form.types'

export type CreationStep = 'create' | 'update' | 'upload' | 'generate' | 'cover' | 'schedule' | 'finish'
export type StepStatus = 'pending' | 'running' | 'done' | 'failed' | 'uncertain'
export type CreationState = 'queued' | 'running' | 'generating' | 'attention' | 'files' | 'unknown' | 'done'

export interface CreationContext {
  account: string
  country: string
}

export interface CreationStore {
  id: string
  name: string
  webId: string
  storeWebId: string
  category: string
  storeLocationTypes?: string[]
}

export interface CreationTask {
  version: 1
  id: string
  context: CreationContext
  store: CreationStore
  platforms: string[]
  tab: LeafletTabData
  leafletId: string | null
  originalNaturalId: string
  steps: Record<CreationStep, StepStatus>
  state: CreationState
  step: CreationStep
  error: string
  createdAt: number
  completedAt: number | null
  uploadAccepted: boolean
  isFromScrapper: boolean
  brochureId: string
}

export const CREATION_STEPS: CreationStep[] = ['create', 'update', 'upload', 'generate', 'cover', 'schedule', 'finish']
export const SUCCESS_RETENTION_MS = 24 * 60 * 60 * 1000

export function sameContext(a: CreationContext | null, b: CreationContext): boolean {
  return a?.account === b.account && a.country === b.country
}

export function copyTab(tab: LeafletTabData, keepFiles = true): LeafletTabData {
  const copy = JSON.parse(JSON.stringify(tab, (key, value) => (key === 'raw' ? undefined : value))) as LeafletTabData
  copy.publishDate = copy.publishDate ? new Date(copy.publishDate) : null
  copy.publicationEndTime = copy.publicationEndTime ? new Date(copy.publicationEndTime) : null
  if (keepFiles) {
    copy.fileList = tab.fileList.map(file => ({ ...file }))
    copy.firstPageFile = tab.firstPageFile.map(file => ({ ...file }))
  }
  return copy
}

export function hasFiles(files: FileItem[]): boolean {
  return files.length > 0 && files.every(file => file.raw instanceof File)
}

export function makeTask(
  input: {
    tab: LeafletTabData
    store: CreationStore
    platforms: string[]
    context: CreationContext
    isFromScrapper?: boolean
    brochureId?: string
  },
  now = Date.now()
): CreationTask {
  const tab = copyTab(input.tab)
  const hasPages = tab.fileList.length > 0
  return {
    version: 1,
    id: crypto.randomUUID(),
    context: { ...input.context },
    store: { ...input.store },
    platforms: [...input.platforms],
    tab,
    leafletId: null,
    originalNaturalId: tab.naturalId,
    steps: {
      create: 'pending',
      update: 'done',
      upload: hasPages ? 'pending' : 'done',
      generate: hasPages ? 'pending' : 'done',
      cover: tab.firstPageFile.length ? 'pending' : 'done',
      schedule: tab.scheduledLongLink.date || tab.scheduledThumbnailTrackingLink.date ? 'pending' : 'done',
      finish: input.isFromScrapper ? 'pending' : 'done'
    },
    state: 'queued',
    step: 'create',
    error: '',
    createdAt: now,
    completedAt: null,
    uploadAccepted: false,
    isFromScrapper: input.isFromScrapper ?? false,
    brochureId: input.brochureId ?? ''
  }
}

export function needsClient(task: CreationTask): boolean {
  return (
    (task.state === 'running' && task.step !== 'generate') ||
    (['queued', 'generating'].includes(task.state) &&
      (['create', 'update', 'upload', 'cover', 'schedule', 'finish'] as CreationStep[]).some(
        step => task.steps[step] === 'pending'
      ))
  )
}
